import React, { useState, useMemo, useEffect } from "react";
import { FaSearch, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoIosInformationCircle } from "react-icons/io";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api_call";
import "./Dashboard.css";

export default function NonApprovedTillDate() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "asc",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const rowsPerPage = 8;

  // ================= FETCH DATA =================
  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    if (!isLoggedIn) {
      navigate("/", { replace: true });
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await apiFetch("/report/admin/score-report");

        // 👇 ONLY NON-APPROVED DATA
        const records = res?.not_approved || [];

        const formatted = records.map((row) => ({
          id: row.id,
          act: row.compliance,
          particular: row.instance,
          title: "",
          description: row.description || "",
          startDate: row.start_date,
          actionDate: row.action_date,
          endDate: row.end_date,
          status: row.status,
          requestDate: row.request_date,
          responseDate: row.response_date,
          userName: row.user_name,
          approversEmail: row.approvers_email,
          bu: row.business_unit || "-",
          department: row.department || "-"
        }));

        setData(formatted);

      } catch (err) {
        console.error("Error fetching score report:", err);
        setError("Failed to fetch non-approved records.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // ================= SEARCH =================
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    const term = searchTerm.toLowerCase();

    return data.filter(
      (item) =>
        item.id?.toString().includes(term) ||
        item.act?.toLowerCase().includes(term) ||
        item.particular?.toLowerCase().includes(term)
    );
  }, [data, searchTerm]);

  // ================= SORT =================
  const sortedData = useMemo(() => {
    let sortable = [...filteredData];

    sortable.sort((a, b) => {
      const valA = (a[sortConfig.key] || "").toString().toLowerCase();
      const valB = (b[sortConfig.key] || "").toString().toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sortable;
  }, [filteredData, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  // ================= COUNTS =================
  const statusCounts = useMemo(() => {
    const counts = {
      pending: 0,
      requested: 0,
      total: 0,
    };

    filteredData.forEach((item) => {
      counts.total++;

      const status = item.status?.toLowerCase();
      if (status === "pending") counts.pending++;
      else if (status === "requested") counts.requested++;
    });

    return counts;
  }, [filteredData]);

  // ================= PAGINATION =================
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // ================= UI =================
  return (
    <div className="general_">
      <Sidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="headSection">
        <div className="compliance-score">Non-Compliant (Till Date)</div>
      </div>

      <div className="table-header" style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: "10px", marginLeft: "45px" }}>
          <button className="btn blue-btn" onClick={() => navigate("/compliant-")}>
            Compliant
          </button>
          <button className="btn gray-btn">
            Non-Compliant
          </button>
        </div>

        <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ background: "#f8d7da", color: "#721c24", padding: "6px 12px", borderRadius: "20px" }}>
              Pending: {statusCounts.pending}
            </div>

            <div style={{ background: "#fff3cd", color: "#856404", padding: "6px 12px", borderRadius: "20px" }}>
              Requested: {statusCounts.requested}
            </div>

            <div style={{ background: "#e2e3e5", padding: "6px 12px", borderRadius: "20px" }}>
              Total: {statusCounts.total}
            </div>
          </div>

          <div className="search-box">
            <input
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch className="search-icon" />
          </div>
        </div>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th onClick={() => requestSort("id")}>Id {getSortIcon("id")}</th>
            <th onClick={() => requestSort("act")}>Compliance {getSortIcon("act")}</th>
            <th>Instance</th>
            <th onClick={() => requestSort("actionDate")}>Action Date</th>
            <th>Status</th>
            <th>User</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr><td colSpan="6">Loading...</td></tr>
          ) : currentRows.map((row, i) => (
            <tr key={i}>
              <td>{row.id}</td>
              <td>{row.act}</td>

              <td>
                {row.particular}
                {row.description && (
                  <IoIosInformationCircle title={row.description} />
                )}
              </td>

              <td>{row.actionDate}</td>

              <td style={{ color: "red", fontWeight: 600 }}>
                {row.status}
              </td>

              <td>{row.userName}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination">
        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
        <span>Page {currentPage} of {totalPages}</span>
        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
      </div>
    </div>
  );
}